import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  FilePresent,
  CalendarToday,
  AttachFile,
  Description
} from '@mui/icons-material';
import { handlePreviewFile } from 'src/services/common';

// Document interface matching the structure from index.tsx
interface Document {
  BidDocsID: number;
  DocumentName: string;
  DocumentType: string;
  DocumentDescription: string;
  InsertDate: string;
  DocumentUrl: string;
}

// Bid details interface for the header information
interface BidDetails {
  contractNo: string;
  title: string;
  categories: string;
  documents?: Document[]; // Add documents array to bid details
  childBidDocuments?: Document[]; // Add documents array to bid details
}

interface UploadedFilesDetailsProps {
  bidId: string;
  bidDetails: BidDetails;
  onBack: () => void;
}

const UploadedFilesDetails: React.FC<UploadedFilesDetailsProps> = ({
  bidDetails,
  onBack
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageDocuments, setPageDocuments] = useState(0);
  const [rowsPerPageDocuments, setRowsPerPageDocuments] = useState(25);
  const [totalCountDocuments, setTotalCountDocuments] = useState(0);
  const [pageChildDocuments, setPageChildDocuments] = useState(0);
  const [rowsPerPageChildDocuments, setRowsPerPageChildDocuments] =
    useState(25);
  const [totalCountChildDocuments, setTotalCountChildDocuments] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Process uploaded files from bid details documents
  const processDocuments = () => {
    if (!bidDetails.documents) {
      return [];
    }

    const bidDocuments = bidDetails.documents.filter(
      (doc) => doc.DocumentDescription === 'Bid Document'
    );

    return bidDocuments.map((doc) => ({
      uploadedDocument: doc.DocumentName,
      documentCaption: doc.DocumentType || doc.DocumentDescription,
      uploadedDate: doc.InsertDate,
      lastModified: doc.InsertDate,
      documentUrl: doc.DocumentUrl
    }));
  };

  const processChildBidDocuments = () => {
    if (!bidDetails.childBidDocuments) {
      return [];
    }

    return bidDetails.childBidDocuments.map((doc) => ({
      uploadedDocument: doc.DocumentName,
      documentCaption: doc.DocumentType,
      uploadedDate: doc.InsertDate,
      lastModified: doc.InsertDate,
      documentUrl: doc.DocumentUrl
    }));
  };

  // Process files when component mounts or bidDetails changes
  useEffect(() => {
    setLoading(true);
    try {
      const processedDocuments = processDocuments();
      setTotalCountDocuments(processedDocuments.length);

      const processedChildDocuments = processChildBidDocuments();
      setTotalCountChildDocuments(processedChildDocuments.length);

      setError(null);
    } catch (err) {
      console.error('Error processing uploaded files:', err);
      setError('Failed to process uploaded files data.');
    } finally {
      setLoading(false);
    }
  }, [bidDetails]);

  // Handle pagination locally since we have all data
  const paginatedDocuments = processDocuments().slice(
    pageDocuments * rowsPerPageDocuments,
    pageDocuments * rowsPerPageDocuments + rowsPerPageDocuments
  );

  const paginatedChildDocuments = processChildBidDocuments().slice(
    pageChildDocuments * rowsPerPageChildDocuments,
    pageChildDocuments * rowsPerPageChildDocuments + rowsPerPageChildDocuments
  );

  const handlePageChangeDocuments = (event: unknown, newPage: number) => {
    setPageDocuments(newPage);
  };

  const handleRowsPerPageChangeDocuments = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPageDocuments(parseInt(event.target.value, 10));
    setPageDocuments(0);
  };

  const handlePageChangeChildDocuments = (event: unknown, newPage: number) => {
    setPageChildDocuments(newPage);
  };

  const handleRowsPerPageChangeChildDocuments = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPageChildDocuments(parseInt(event.target.value, 10));
    setPageChildDocuments(0);
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Handle document download
  // const handleDocumentDownload = (
  //   documentUrl: string,
  //   documentName: string
  // ) => {
  //   if (documentUrl) {
  //     const link = document.createElement('a');
  //     link.href = documentUrl;
  //     link.download = documentName;
  //     link.target = '_blank';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   }
  // };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 80,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Uploaded Files Details
        </Typography>
      </Box>

      {/* Bid Information Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 3
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Title: {bidDetails.title}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5
                }}
              >
                Categories: {bidDetails.categories}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Uploaded Files Section */}
      {bidDetails?.documents?.length > 0 && (
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
          }}
        >
          <Box
            sx={{
              p: 2.5,
              bgcolor: `linear-gradient(to right, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, ${alpha(theme.palette.primary.main, 0.02)})`,
              borderBottom: `1px solid ${alpha(
                theme.palette.primary.main,
                0.1
              )}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              Uploaded Files
            </Typography>
            {!loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Total Files: {totalCountDocuments}
              </Typography>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      py: 2
                    },
                    '& .MuiTableCell-body': {
                      py: 1.8
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Uploaded Document</TableCell>
                      <TableCell>Document Caption</TableCell>
                      <TableCell>Uploaded Date</TableCell>
                      <TableCell>Last Modified</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <FilePresent
                              sx={{
                                fontSize: 40,
                                color: alpha(theme.palette.text.secondary, 0.6)
                              }}
                            />
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              No uploaded files found for this bid.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedDocuments.map((file, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.03)
                            },
                            transition: 'background-color 0.2s ease',
                            '&:nth-of-type(even)': {
                              bgcolor: alpha(
                                theme.palette.background.default,
                                0.4
                              )
                            }
                          }}
                        >
                          <TableCell>
                            {file.documentUrl ? (
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<AttachFile />}
                                onClick={() =>
                                  handlePreviewFile(file.documentUrl)
                                }
                                sx={{
                                  color: theme.palette.primary.main,
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  justifyContent: 'flex-start',
                                  '&:hover': {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    )
                                  }
                                }}
                              >
                                {file.uploadedDocument || '-'}
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Description
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.primary.main
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {file.uploadedDocument || '-'}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={file.documentCaption || '-'}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.text.secondary
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {formatDateTime(file.uploadedDate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.text.secondary
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {formatDateTime(file.lastModified)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination for Documents */}
              <Box
                sx={{
                  borderTop: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.5)
                }}
              >
                <TablePagination
                  component="div"
                  count={totalCountDocuments}
                  page={pageDocuments}
                  onPageChange={handlePageChangeDocuments}
                  rowsPerPage={rowsPerPageDocuments}
                  onRowsPerPageChange={handleRowsPerPageChangeDocuments}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                      {
                        fontWeight: 500
                      },
                    '& .MuiTablePagination-select': {
                      borderRadius: 1.5,
                      '&:focus': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          )
                        }
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      )}

      {/* Uploaded Addendum Files */}
      {bidDetails?.childBidDocuments?.length > 0 && (
        <Card
          sx={{
            marginTop: '22px',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
          }}
        >
          <Box
            sx={{
              p: 2.5,
              bgcolor: `linear-gradient(to right, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, ${alpha(theme.palette.primary.main, 0.02)})`,
              borderBottom: `1px solid ${alpha(
                theme.palette.primary.main,
                0.1
              )}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              Uploaded Addendum Files
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      py: 2
                    },
                    '& .MuiTableCell-body': {
                      py: 1.8
                    }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Uploaded Document</TableCell>
                      <TableCell>Document Caption</TableCell>
                      <TableCell>Uploaded Date</TableCell>
                      <TableCell>Last Modified</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedChildDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2
                            }}
                          >
                            <FilePresent
                              sx={{
                                fontSize: 40,
                                color: alpha(theme.palette.text.secondary, 0.6)
                              }}
                            />
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              No uploaded files found for this bid.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedChildDocuments.map((file, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            {file.documentUrl ? (
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<AttachFile />}
                                onClick={() =>
                                  handlePreviewFile(file.documentUrl)
                                }
                                sx={{
                                  color: theme.palette.primary.main,
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  justifyContent: 'flex-start',
                                  '&:hover': {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    )
                                  }
                                }}
                              >
                                {file.uploadedDocument || '-'}
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Description
                                  sx={{
                                    fontSize: 18,
                                    color: theme.palette.primary.main
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {file.uploadedDocument || '-'}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={file.documentCaption || '-'}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.text.secondary
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {formatDateTime(file.uploadedDate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.text.secondary
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {formatDateTime(file.lastModified)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination for Child Documents */}
              <Box
                sx={{
                  borderTop: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.5)
                }}
              >
                <TablePagination
                  component="div"
                  count={totalCountChildDocuments}
                  page={pageChildDocuments}
                  onPageChange={handlePageChangeChildDocuments}
                  rowsPerPage={rowsPerPageChildDocuments}
                  onRowsPerPageChange={handleRowsPerPageChangeChildDocuments}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                      {
                        fontWeight: 500
                      },
                    '& .MuiTablePagination-select': {
                      borderRadius: 1.5,
                      '&:focus': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          )
                        }
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      )}
    </Box>
  );
};

export default UploadedFilesDetails;
